import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NumberParam, StringParam, useQueryParam } from 'use-query-params';
import { Button, FilterBar, SearchComponent, StartPage } from '../../../components';
import Dialog from '../../../components/dialog';
import UniversalModal from '../../../components/modal-alerts/universal-modal';
import { billingService } from '../../../api';
import { useUi } from '../../../context/ui.context';
import { usePageTitle } from '../../../context/navigationContext';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import {
  executeOrderAction,
  formatDateTime,
  getApiErrorMessage,
  getDisplayName,
  getEntityId,
  getOrderActions,
  getOrderStatusLabel,
  OrderAction,
} from './helpers';
import { OrderStatusText } from './styles';

const DEFAULT_ORDERING = '-order_date';
const ORDER_FILTER_KEYS = ['client', 'user', 'order_date', 'status'];
const ORDER_FILTER_TYPES: Record<string, 'date' | 'number' | 'text' | undefined> = {
  client: undefined,
  user: undefined,
  order_date: 'date',
  status: undefined,
};

const sameData = (first: any, second: any) => JSON.stringify(first) === JSON.stringify(second);

const Orders: FC = () => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  usePageTitle(t('PAGE_TITLE_ORDERS'));

  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize] = useQueryParam('page_size', NumberParam);
  const [searchString, setSearchString] = useQueryParam('search', StringParam);
  const [filtering, setFiltering] = useQueryParam('filtering', StringParam);

  const [orders, setOrders] = useState<any[]>([]);
  const [paginationData, setPaginationData] = useState<any>();
  const [filterOptions, setFilterOptions] = useState<any>();
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pendingOrder, setPendingOrder] = useState<any>();
  const [pendingAction, setPendingAction] = useState<OrderAction>();
  const [actionLoading, setActionLoading] = useState(false);

  const normalizedFilterOptions = useMemo(() => {
    return ORDER_FILTER_KEYS.reduce((acc:any, key) => {
      acc[key] = {
        options: filterOptions?.[key] || [],
        type: ORDER_FILTER_TYPES[key],
      };
      return acc;
    }, {});
  }, [filterOptions]);

  const effectiveOrdering = ordering || DEFAULT_ORDERING;

  const getOrders = useCallback((options: { silent?: boolean } = {}) => {
    if (!options.silent) {
      setLoading(true);
    }
    billingService.getOrders({
      page: page || 1,
      page_size: pageSize || 10,
      ordering: effectiveOrdering,
      search: searchString || '',
      filtering: filtering || '',
    })
      .then((res:any) => {
        const data = res.data?.object || res.data;
        const nextOrders = data?.objects || data?.results || [];
        setOrders((prev) => sameData(prev, nextOrders) ? prev : nextOrders);
        setPaginationData((prev:any) => sameData(prev, data) ? prev : data);
        setFilterOptions((prev:any) => sameData(prev, data?.filter_options) ? prev : data?.filter_options);
      })
      .catch((error:any) => {
        viewAlert({
          severity: 'error',
          message: getApiErrorMessage(error, t('ERROR_OCCURRED')),
        });
      })
      .finally(() => {
        setPageLoading(false);
        if (!options.silent) {
          setLoading(false);
        }
      });
  }, [effectiveOrdering, filtering, page, pageSize, searchString, t, viewAlert]);

  useEffect(() => {
    getOrders();
    const interval = setInterval(() => getOrders({ silent: true }), 10000);
    return () => clearInterval(interval);
  }, [getOrders]);

  useUpdateEffect(() => {
    setPage(1);
  }, [pageSize, filtering]);

  const openAction = (order: any, action: OrderAction) => {
    setPendingOrder(order);
    setPendingAction(action);
  };

  const closeAction = () => {
    setPendingOrder(undefined);
    setPendingAction(undefined);
  };

  const handleActionConfirm = async () => {
    if (!pendingOrder || !pendingAction) return;
    setActionLoading(true);
    try {
      await executeOrderAction(pendingAction.id, pendingOrder.id);
      viewAlert({
        severity: 'success',
        message: pendingAction.successMessage,
      });
      closeAction();
      getOrders();
    } catch (error:any) {
      viewAlert({
        severity: 'error',
        message: getApiErrorMessage(error, t('ERROR_OCCURRED')),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetFilters = () => {
    setSearchString(undefined);
    setFiltering(undefined);
    setOrdering(DEFAULT_ORDERING);
    setPage(1);
  };

  const orderRows = useMemo(() => {
    return orders.map((order:any) => ({
      ...order,
      id: order.id,
      client: getEntityId(order.client) ? (
        <a href={`/clients-users/clients/${getEntityId(order.client)}`}>{getDisplayName(order.client)}</a>
      ) : getDisplayName(order.client),
      user: getEntityId(order.user) ? (
        <a href={`/clients-users/users/${getEntityId(order.user)}`}>{getDisplayName(order.user)}</a>
      ) : getDisplayName(order.user),
      order_date: formatDateTime(order.order_date),
      status: (
        <OrderStatusText status={order.status}>
          {getOrderStatusLabel(order.status, t)}
        </OrderStatusText>
      ),
      payment_status: order.invoice?.status || 'n/a',
      navigate: order.id?.toString(),
      dropOptions: getOrderActions(order, t).map((action) => ({
        ...action,
        onClick: () => openAction(order, action),
      })),
    }));
  }, [orders, t]);

  const columns = [
    { title: t('ORDER_NUMBER'), key: 'id', sorted: true },
    { title: t('CLIENT'), key: 'client', sorted: true },
    { title: t('USER'), key: 'user', sorted: true },
    { title: t('ORDER_DATE'), key: 'order_date', sorted: true },
    { title: t('STATUS'), key: 'status', sorted: true },
    { title: t('PAYMENT_STATUS'), key: 'payment_status', sorted: true },
    { title: '', key: 'props' },
  ];

  return (
    <>
      <StartPage
        title={t('ORDERS') as string}
        noResourceText={t('NO_ORDERS') as string}
        haveResource={orderRows.length > 0}
        pageLoading={pageLoading}
        withoutCreateResourceButton
        rightCustomCreateButton={
          <div style={{display:'flex', flexDirection:'column', gap:'10px', width:'100%'}}>
            <div style={{display:'flex', alignItems:'center', gap:'12px', width:'100%'}}>
              <div style={{flex:1, minWidth:'200px'}}>
                <SearchComponent />
              </div>
              <Button
                title={t('CLEAR_FILTERS')}
                onClick={handleResetFilters}
                variant='stroke'
                size='display'
                disabled={
                  (!filtering || filtering === '') &&
                  !searchString &&
                  effectiveOrdering === DEFAULT_ORDERING
                }
              />
            </div>
            {normalizedFilterOptions ? <FilterBar filterOptions={normalizedFilterOptions} /> : null}
          </div>
        }
        tableData={{
          columns,
          data: orderRows,
          paginationData,
          updateData: getOrders,
          loading,
          hideCheckbox: true,
        }}
      />
      <Dialog
        open={Boolean(pendingAction)}
        active={Boolean(pendingAction)}
        noIframe
        handleClose={closeAction}
      >
        <UniversalModal
          closeModal={handleActionConfirm}
          closeModalCancel={closeAction}
          alertTitle={pendingAction?.title}
          alertSubTitle={pendingAction?.message}
          loading={actionLoading}
        />
      </Dialog>
    </>
  );
};

export default Orders;
